import { useQuery } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { apiGet } from "../api";
import type { SeasonPhase } from "./memberships";
import type { ID } from "../id";

/**
 * Dashboard figures for one season. `season` is null when no season is
 * active and none was requested — a 200 with zeroed season blocks, so a
 * federation that has not opened its first season still renders.
 *
 * `members.registered` and `members.guests` partition: guests counts people
 * with a guest membership and NO registered one this season, so the two can
 * be added without double-counting someone who is a člen of one team and a
 * nečlen of another.
 *
 * `teams.active` / `pending` / `suspended` are NOT season-scoped and cannot
 * be — a Team has no season. `with_roster` and `paid` are, and they use the
 * same predicate as the `team_unpaid` alert, so `with_roster - paid` equals
 * that alert's count.
 */
export interface DashboardStats {
  season: {
    id: ID;
    code: string;
    active: boolean;
    phase: SeasonPhase;
    /** season.FeeDue: the fee is owed (from the uzávěrka on). */
    fee_due: boolean;
  } | null;
  members: { registered: number; guests: number; with_photo: number };
  teams: {
    active: number;
    pending: number;
    suspended: number;
    with_roster: number;
    paid: number;
  };
  competitions: {
    total: number;
    draft: number;
    announced: number;
    published: number;
    running: number;
    finished: number;
    archived: number;
    upcoming: number;
    entries_open: number;
  };
  entries: { submitted: number; withdrawn: number };
  global: { federations: number; users: number; persons: number };
  system: {
    version: string;
    started_at: Dayjs;
    server_now: Dayjs;
  };
}

/**
 * The endpoint is `admin` / `members_admin` only — pass `enabled` rather
 * than merely hiding the card, or every other role fires a 403 per load.
 */
export function useDashboardStats(options?: { enabled?: boolean; seasonId?: ID }) {
  const seasonId = options?.seasonId;
  return useQuery<DashboardStats>({
    queryKey: ["dashboard", "stats", seasonId ?? "active"],
    queryFn: () =>
      apiGet<DashboardStats>(`/dashboard/stats${seasonId ? `?season_id=${seasonId}` : ""}`),
    enabled: options?.enabled ?? true,
  });
}

// Alert kinds mirror internal/service/dashboard/alerts.go and
// alerts_competition.go. The Czech copy and CTAs are rendered on the
// frontend (see DashboardPage/alertCopy).
//
// Adding a kind in Go without adding it here AND a `case` in alertCopy.ts
// renders a blank „Upozornění" with no type error and no failing test.
export type AlertKind =
  | "no_active_season"
  | "teams_pending_approval"
  | "registration_opens"
  | "registration_closing"
  | "roster_not_transferred"
  | "roster_empty"
  | "season_registration_draft"
  | "team_unpaid"
  | "team_not_paid"
  | "members_missing_photo"
  | "team_documents_missing"
  | "teams_documents_missing"
  | "competition_deadline_near"
  | "competition_no_entries"
  | "competition_draft_soon"
  | "competition_entries_empty"
  | "competition_rule_set_not_active"
  | "competition_music_missing"
  | "competition_withdrawn"
  | "mail_misconfigured";

export type AlertSeverity = "info" | "warn" | "err";

export interface DashboardAlert {
  kind: AlertKind;
  severity: AlertSeverity;
  data: Record<string, unknown>;
}

export function useDashboardAlerts() {
  return useQuery<DashboardAlert[]>({
    queryKey: ["dashboard", "alerts"],
    queryFn: async () => {
      const res = await apiGet<{ alerts: DashboardAlert[] }>("/dashboard/alerts");
      return res.alerts;
    },
  });
}
